import { useEffect, useMemo, useRef } from "react";
import {
  useForm,
  useWatch,
  type DefaultValues,
  type FieldValues,
  type Resolver,
  type SubmitHandler,
} from "react-hook-form";
import { createFormJsonSchemaResolver } from "containers/admin/clients/client/components/form-builder/json-schema-resolver";

import { createFormBuilderFields } from "containers/admin/clients/client/components/form-builder/form-builder-fields";
import type { FormBuilderActions } from "containers/admin/clients/client/components/form-builder/form-builder-action-button";
import {
  getConditionalRules,
  mergeDefaultValues,
  normalizeJsonFormSchema,
  sortJsonFormFields,
  type DynamicFormSchema,
  type FormPageSchemaTab,
} from "containers/admin/clients/client/components/form-builder/form-schema";

export type {
  DynamicFormSchema,
  FormPageSchemaTab,
  FormSchemaSidebarItem,
  JsonFormObjectSchema,
  JsonFormOption,
  JsonFormOptionValue,
  JsonFormSchema,
  JsonFormSection,
  JsonFormSectionHeadingLevel,
  JsonFormUiType,
} from "containers/admin/clients/client/components/form-builder/form-schema";

export {
  cleanupFormDataForSave,
  createFormSchemaFromSections,
  deriveDefaultValues,
  getFormSchemaSidebarItems,
  getFormSchemaTabs,
  getNestedValue,
  getSectionInfo,
  getSectionLayout,
  isEmptyFormValue,
  mergeDefaultValues,
  mergeTabFormData,
  normalizeJsonFormSchema,
  setNestedValue,
} from "containers/admin/clients/client/components/form-builder/form-schema";

interface FormBuilderProps<T extends FieldValues = FieldValues> {
  schema: DynamicFormSchema;
  defaultValues?: T;
  onSubmit: (data: T) => void;
  onValuesChange?: (data: T) => void;
  formId?: string;
  actions?: FormBuilderActions;
}

const FormBuilder = <T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  onValuesChange,
  formId,
  actions,
}: FormBuilderProps<T>) => {
  const normalizedSchema = useMemo(() => normalizeJsonFormSchema(schema), [schema]);
  const resolver = useMemo(
    () => createFormJsonSchemaResolver(normalizedSchema),
    [normalizedSchema],
  );
  const resolvedDefaults = mergeDefaultValues(normalizedSchema, defaultValues) as T;
  const conditionalRules = useMemo(
    () => getConditionalRules(normalizedSchema),
    [normalizedSchema],
  );
  const dependentFieldNames = useMemo(
    () => new Set(conditionalRules.flatMap((rule) => rule.dependentFieldNames)),
    [conditionalRules],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<T>({
    resolver: resolver as Resolver<T>,
    mode: "onSubmit",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: resolvedDefaults as DefaultValues<T>,
    shouldUnregister: false,
  });

  const formData = useWatch({ control });
  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;
  const skipInitialValuesChangeRef = useRef(Boolean(onValuesChange));

  useEffect(() => {
    if (skipInitialValuesChangeRef.current) {
      skipInitialValuesChangeRef.current = false;
      return;
    }

    onValuesChangeRef.current?.(formData as T);
  }, [formData]);

  const { renderField } = useMemo(
    () =>
      createFormBuilderFields<T>({
        control,
        errors,
        formData,
        actions,
      }),
    [actions, control, errors, formData],
  );

  const onFormSubmit: SubmitHandler<T> = (data) => {
    onSubmit(data);
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onFormSubmit)}>
      <div className="flex flex-col gap-5">
        {sortJsonFormFields(Object.entries(normalizedSchema.properties))
          .filter(([name]) => !dependentFieldNames.has(name))
          .map(([name, field]) => renderField(name, field, normalizedSchema))}
      </div>
    </form>
  );
};

export default FormBuilder;
